import type { MailProviderClient, NormalizedMessage, NormalizedAttachment } from "./types";
import { ProviderError } from "./types";

export class OutlookClient implements MailProviderClient {
  provider = "outlook" as const;

  constructor(private accessToken: string) {}

  private async fetch<T>(url: string): Promise<T> {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        Prefer: 'outlook.body-content-type="text"',
      },
    });
    if (res.status === 401 || res.status === 403) {
      throw new ProviderError(`Outlook auth error: ${res.status}`, "token_expired");
    }
    if (res.status === 429) {
      const retryAfter = res.headers.get("Retry-After");
      throw new ProviderError(`Outlook rate limited${retryAfter ? `, retry after ${retryAfter}s` : ""}`, "rate_limited");
    }
    if (!res.ok) {
      throw new ProviderError(`Outlook API error: ${res.status}`, "unknown");
    }
    return res.json();
  }

  async getProfile(): Promise<{ emailAddress: string }> {
    const data = await this.fetch<{ mail?: string; userPrincipalName?: string }>(
      "https://graph.microsoft.com/v1.0/me?$select=mail,userPrincipalName"
    );
    return { emailAddress: data.mail || data.userPrincipalName || "unknown" };
  }

  async listMessagesWithAttachments(
    query = "hasAttachments eq true",
    pageToken?: string
  ): Promise<{
    messages: NormalizedMessage[];
    attachments: NormalizedAttachment[];
    nextPageToken?: string;
  }> {
    const params = new URLSearchParams({
      $select: "id,subject,from,receivedDateTime,bodyPreview,conversationId",
      $filter: query,
      $top: "50",
    });
    if (pageToken) params.set("$skiptoken", pageToken);

    const listData = await this.fetch<{
      value?: {
        id: string;
        conversationId: string;
        subject?: string;
        bodyPreview?: string;
        from?: { emailAddress?: { name?: string; address?: string } };
        receivedDateTime?: string;
      }[];
      "@odata.nextLink"?: string;
    }>(`https://graph.microsoft.com/v1.0/me/messages?${params}`);

    return this.processMessageList(listData);
  }

  async listDelta(
    deltaToken?: string
  ): Promise<{
    messages: NormalizedMessage[];
    attachments: NormalizedAttachment[];
    deltaToken?: string;
    nextPageToken?: string;
  }> {
    const url = deltaToken
      ? `https://graph.microsoft.com/v1.0/me/messages/delta?$deltatoken=${encodeURIComponent(deltaToken)}`
      : "https://graph.microsoft.com/v1.0/me/messages/delta?$select=id,subject,from,receivedDateTime,bodyPreview,conversationId";

    const data = await this.fetch<{
      value?: {
        id: string;
        conversationId: string;
        subject?: string;
        bodyPreview?: string;
        from?: { emailAddress?: { name?: string; address?: string } };
        receivedDateTime?: string;
      }[];
      "@odata.nextLink"?: string;
      "@odata.deltaLink"?: string;
    }>(url);

    const result = await this.processMessageList(data);

    // Extract delta token from deltaLink
    let nextDeltaToken: string | undefined;
    if (data["@odata.deltaLink"]) {
      const deltaUrl = new URL(data["@odata.deltaLink"]);
      nextDeltaToken = deltaUrl.searchParams.get("$deltatoken") || undefined;
    }

    // Extract skip token from nextLink for pagination
    let nextPageToken: string | undefined;
    if (data["@odata.nextLink"]) {
      const nextUrl = new URL(data["@odata.nextLink"]);
      nextPageToken = nextUrl.searchParams.get("$skiptoken") || undefined;
    }

    return { ...result, deltaToken: nextDeltaToken, nextPageToken };
  }

  private async processMessageList(listData: {
    value?: {
      id: string;
      conversationId: string;
      subject?: string;
      bodyPreview?: string;
      from?: { emailAddress?: { name?: string; address?: string } };
      receivedDateTime?: string;
    }[];
    "@odata.nextLink"?: string;
  }): Promise<{
    messages: NormalizedMessage[];
    attachments: NormalizedAttachment[];
    nextPageToken?: string;
  }> {
    const messages: NormalizedMessage[] = [];
    const attachments: NormalizedAttachment[] = [];

    if (!listData.value) return { messages, attachments };

    for (const msg of listData.value) {
      try {
        const fromAddr = msg.from?.emailAddress;
        messages.push({
          id: msg.id,
          threadId: msg.conversationId,
          subject: msg.subject || "(no subject)",
          snippet: msg.bodyPreview || "",
          senderName: fromAddr?.name || "",
          senderEmail: fromAddr?.address || "",
          receivedAt: msg.receivedDateTime ? new Date(msg.receivedDateTime).toISOString() : new Date().toISOString(),
          labels: [],
        });

        const attData = await this.fetch<{
          value?: {
            id: string;
            name?: string;
            contentType?: string;
            size?: number;
          }[];
        }>(`https://graph.microsoft.com/v1.0/me/messages/${msg.id}/attachments?$select=id,name,contentType,size`);

        for (const att of attData.value || []) {
          if (att.name) {
            attachments.push({
              messageId: msg.id,
              attachmentId: att.id,
              filename: att.name,
              mimeType: att.contentType || "application/octet-stream",
              size: att.size || 0,
            });
          }
        }
      } catch (err) {
        if (err instanceof ProviderError) throw err;
        console.warn("Failed to fetch message/attachments", msg.id, err);
      }
    }

    let nextPageToken: string | undefined;
    if (listData["@odata.nextLink"]) {
      const nextUrl = new URL(listData["@odata.nextLink"]);
      nextPageToken = nextUrl.searchParams.get("$skiptoken") || undefined;
    }

    return { messages, attachments, nextPageToken };
  }

  async fetchAttachment(messageId: string, attachmentId: string): Promise<Blob> {
    const res = await fetch(
      `https://graph.microsoft.com/v1.0/me/messages/${messageId}/attachments/${attachmentId}/$value`,
      { headers: { Authorization: `Bearer ${this.accessToken}` } }
    );
    if (res.status === 401 || res.status === 403) {
      throw new ProviderError(`Outlook auth error: ${res.status}`, "token_expired");
    }
    if (!res.ok) {
      throw new ProviderError(`Outlook API error: ${res.status}`, "unknown");
    }
    return res.blob();
  }
}
